import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  Check,
  Copy,
  Download,
  Loader2,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils/cn';

export interface BackupCodesPanelProps {
  /** Kratos settings flow */
  flow: any;
  identifier: string;
  csrfToken: string;
  isSubmitting: boolean;
  submitError?: string | null;
  /** Triggers fetch with method=lookup_secret + lookup_secret_regenerate=true */
  onRegenerate: () => Promise<void>;
  /** Triggers fetch with method=lookup_secret + lookup_secret_confirm=true */
  onConfirm: () => Promise<void>;
}

/**
 * Reads the lookup_secret nodes from the settings flow and renders:
 *  - Warning banner (codes only shown once).
 *  - Grid with codes (copy + download).
 *  - Regenerate button (invalidates existing codes — confirm modal).
 *  - Confirm button (acknowledges save → finalizes activation).
 *
 * The submit side (fetch) lives in the parent, identical to TotpSetupForm's
 * pattern, so we keep error handling in one place.
 */
export function BackupCodesPanel({
  flow,
  identifier,
  csrfToken,
  isSubmitting,
  submitError,
  onRegenerate,
  onConfirm,
}: BackupCodesPanelProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = React.useState(false);
  const [showConfirmRegenerate, setShowConfirmRegenerate] = React.useState(false);

  const codesNode = flow?.ui?.nodes?.find(
    (n: any) =>
      n.group === 'lookup_secret' &&
      n.attributes?.id === 'lookup_secret_codes'
  );
  const codesText = codesNode?.attributes?.text?.text as string | undefined;
  // Some Kratos versions return text as a comma-separated list inside
  // `attributes.text.text`. Others put it in `meta.label.text`. Handle both.
  const codesRaw =
    codesText ?? (codesNode?.meta?.label as any)?.text ?? '';
  const codes = codesRaw
    .split(/[,\s]+/)
    .map((c: string) => c.trim())
    .filter((c: string) => c.length > 0);

  // Some flows reveal the codes only after pressing "Generate" — handle the
  // pre-reveal state with an empty placeholder + the regenerate CTA.
  const hasCodes = codes.length > 0;
  const regenerateNode = flow?.ui?.nodes?.find(
    (n: any) => n.attributes?.name === 'lookup_secret_regenerate'
  );
  const confirmNode = flow?.ui?.nodes?.find(
    (n: any) => n.attributes?.name === 'lookup_secret_confirm'
  );

  React.useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  const copyAll = async () => {
    if (codes.length === 0) return;
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
    } catch {
      // ignore
    }
  };

  const downloadTxt = () => {
    if (codes.length === 0) return;
    const blob = new Blob(
      [
        `WYA Solutions — Backup codes\n`,
        `Account: ${identifier}\n`,
        `Generated: ${new Date().toISOString()}\n`,
        `\n`,
        `Each code can be used ONCE. Keep them in a safe place.\n`,
        `\n`,
        codes.join('\n'),
        `\n`,
      ],
      { type: 'text/plain' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wya-backup-codes-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Hidden fields (parent submit reads them — but keep here for forms that opt to be native) */}
      <input type="hidden" name="csrf_token" value={csrfToken} readOnly />
      <input type="hidden" name="method" value="lookup_secret" readOnly />
      <input type="hidden" name="traits.email" value={identifier} readOnly />

      {/* Critical warning: codes shown once */}
      <Alert className="border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100">
        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-500" />
        <AlertTitle className="text-sm font-medium">
          {t('auth.mfaBackupCodesSavedWarning')}
        </AlertTitle>
        <AlertDescription className="text-xs">
          {t('auth.mfaBackupCodesIntro')}
        </AlertDescription>
      </Alert>

      {/* Codes grid */}
      {hasCodes ? (
        <div className="rounded-lg border border-input bg-muted/30 p-4">
          <div className="grid grid-cols-2 gap-2">
            {codes.map((code: string, i: number) => (
              <code
                key={i}
                className={cn(
                  'select-all rounded-md border border-input bg-background',
                  'px-2.5 py-1.5 font-mono text-xs tracking-wider text-center'
                )}
              >
                {code}
              </code>
            ))}
          </div>

          <div className="mt-3 flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={copyAll}
              disabled={isSubmitting}
              className="flex-1"
            >
              {copied ? (
                <>
                  <Check className="mr-1.5 h-3.5 w-3.5 text-green-600" />
                  {t('common.copied')}
                </>
              ) : (
                <>
                  <Copy className="mr-1.5 h-3.5 w-3.5" />
                  {t('auth.mfaBackupCodesCopy')}
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={downloadTxt}
              disabled={isSubmitting}
              className="flex-1"
            >
              <Download className="mr-1.5 h-3.5 w-3.5" />
              {t('auth.mfaBackupCodesDownload')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-input bg-muted/20 p-6 text-center">
          <p className="text-sm text-muted-foreground">
            {t('auth.mfaBackupCodesNoneYet')}
          </p>
        </div>
      )}

      {submitError && (
        <Alert variant="destructive">
          <AlertDescription className="text-sm">{submitError}</AlertDescription>
        </Alert>
      )}

      {/* Actions */}
      <div className="space-y-2">
        {confirmNode && hasCodes && (
          <Button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting}
            className="w-full h-11"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('auth.settingsSubmitting')}
              </>
            ) : (
              <>
                <ShieldCheck className="mr-2 h-4 w-4" />
                {t('auth.mfaBackupCodesConfirm')}
              </>
            )}
          </Button>
        )}

        {regenerateNode && (
          <>
            {!showConfirmRegenerate ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowConfirmRegenerate(true)}
                disabled={isSubmitting}
                className="w-full h-11"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                {hasCodes
                  ? t('auth.mfaBackupCodesRegenerate')
                  : t('auth.mfaBackupCodesGenerate')}
              </Button>
            ) : (
              <Alert variant="destructive">
                <AlertTitle className="text-sm">
                  {t('auth.mfaBackupCodesRegenerateConfirmTitle')}
                </AlertTitle>
                <AlertDescription className="text-xs mb-2">
                  {t('auth.mfaBackupCodesRegenerateConfirmDescription')}
                </AlertDescription>
                <div className="flex gap-2 mt-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowConfirmRegenerate(false)}
                    disabled={isSubmitting}
                    className="flex-1"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      setShowConfirmRegenerate(false);
                      void onRegenerate();
                    }}
                    disabled={isSubmitting}
                    className="flex-1"
                  >
                    {t('auth.mfaBackupCodesRegenerateConfirm')}
                  </Button>
                </div>
              </Alert>
            )}
          </>
        )}
      </div>
    </div>
  );
}
